import {CreateOperationalEntityInput} from "@common-core/coat-operational-hierarchy-appsync-model";
import {CreateResultNode} from "../view-model";

const transformToSkipped = (
  input: CreateOperationalEntityInput
): CreateResultNode => ({
  status: "skipped",
  message: "Parent creation failed",
  caId: input.sources[0].sourceId,
  dbaName: input.organizationInformation.dbaName,
  children: input.children?.map(transformToSkipped) || []
});

const transform = (
  input: CreateOperationalEntityInput,
  foundFailure: boolean
): CreateResultNode => {
  // If node has an id, it already exists and is operational
  if (input.id != null) {
    return {
      status: "operational",
      oeId: input.id,
      caId: input.sources[0].sourceId,
      dbaName: input.organizationInformation.dbaName,
      children:
        input.children?.map(child => transform(child, foundFailure)) || []
    };
  }

  // If this is the first node without an id and we haven't found a failure yet
  if (!foundFailure) {
    return {
      status: "failed",
      message: "Gateway Timeout",
      caId: input.sources[0].sourceId,
      dbaName: input.organizationInformation.dbaName,
      children: input.children?.map(transformToSkipped) || []
    };
  }

  // This node and all its children should be skipped
  return transformToSkipped(input);
};

/**
 * Transform a CreateOperationalEntityInput tree into a CreateResultNode tree
 * for failure scenarios. Nodes with existing ids are marked as "operational"
 * (they already exist and cannot fail). The first node without an id is marked
 * as "failed", and all its descendants are marked as "skipped" due to parent failure.
 *
 * Contract:
 * - Nodes with id property set get status "operational" (already exist)
 * - First node without id gets status "failed"
 * - All children of failed node get status "skipped" with message "Parent creation failed"
 * - oeId is set to id value for operational nodes, empty string for others
 * - caId comes from input.sources[0].sourceId (first source)
 * - dbaName comes from input.organizationInformation.dbaName
 *
 * This function is pure and recursive; it does not mutate the input.
 */
export const toFailedResults = (
  input: CreateOperationalEntityInput
): CreateResultNode => {
  return transform(input, false);
};
